<script setup lang="ts">
import { Popover, PopoverButton, PopoverPanel, TransitionRoot } from "@headlessui/vue";
import { useFloating, autoPlacement } from "@floating-ui/vue";
import { ref } from "vue";

interface Props {
  id: string;
  name: string;
  singular: string;
  plural: string;
  disabled: boolean;
  typedEntriesCount: number;
  searchedForEntriesCount: number;
  placeholder: string;
}

const searchTerms = defineModel<string>({ default: "" });

defineProps<Props>();
const emit = defineEmits<{
  (event: "search"): void;
  (event: "clear"): void;
}>();

const reference = ref(null);
const floating = ref(null);
const { floatingStyles } = useFloating(reference, floating, {
  strategy: "absolute",
  middleware: [
    autoPlacement({
      alignment: "start",
      autoAlignment: true,
      allowedPlacements: ["bottom-start", "bottom", "bottom-end"],
    }),
  ],
});

async function search(cb: () => void) {
  emit("search");
  cb();
}
</script>

<template>
  <Popover v-slot="{ open }" as="div" class="relative w-full">
    <PopoverButton
      v-bind="$attrs"
      ref="reference"
      :disabled="disabled"
      class="placeholder: w-full focus-visible:outline-0 disabled:cursor-not-allowed"
    >
      <button
        :disabled="disabled"
        :is-open="open"
        :class="{
          'rounded-md border-b': !open,
          'rounded-none rounded-tl-md rounded-tr-md border-b-0': open,
        }"
        class="border-primary text-primary hover:bg-accent/50 inline-flex h-8 w-full cursor-pointer items-center justify-between gap-3 rounded-md border p-3 text-base font-medium transition-colors focus:shadow-inner active:shadow-inner disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span v-if="searchedForEntriesCount === 1">{{ searchTerms.split("\n")[0] }}</span>
        <span v-else-if="searchedForEntriesCount > 1" class="rounded-full bg-gray-300 px-4 text-sm">
          {{ searchedForEntriesCount + " " + plural }} Selected</span
        >
        <span v-else>{{ singular }} Search</span>
        <div class="flex items-center gap-2">
          <span
            v-if="searchedForEntriesCount <= 1"
            class="bg-secondary text-secondary-foreground z-20 ml-4 rounded-full px-4 text-sm"
            :class="{ 'bg-accent text-accent-foreground': searchedForEntriesCount === 0 }"
          >
            {{ searchedForEntriesCount }}
          </span>
          <span class="text-sm">
            <i class="fa-regular fa-pen-field" />
          </span>
        </div>
      </button>
    </PopoverButton>
    <TransitionRoot :show="open" as="div" class="relative">
      <PopoverPanel
        ref="floating"
        v-slot="{ close }"
        :style="floatingStyles"
        static
        :class="{
          '!rounded-none': open,
        }"
        class="bg-card absolute z-40 w-full rounded-md"
      >
        <FormKit
          :id="id"
          v-model="searchTerms"
          :name="name"
          type="textarea"
          :classes="{
            outer: '!mb-0',
            inner: '!mb-0 !ring-0 !border !border-r !border-primary !rounded-none',
            input: 'placeholder:whitespace-pre-wrap',
          }"
          :placeholder="placeholder"
        />
        <div>
          <p class="border-primary bg-background text-foreground border-r border-l text-center text-sm font-semibold">
            {{ typedEntriesCount }} {{ typedEntriesCount === 1 ? singular : plural }}
          </p>
        </div>
        <button
          v-if="searchedForEntriesCount > 0"
          class="border-primary h-9 w-full border-t border-r border-l text-center font-semibold text-red-500 active:shadow-inner"
          @click.prevent="$emit('clear')"
        >
          Reset
        </button>
        <button
          class="bg-primary text-primary-foreground hover:bg-primary/80 h-9 w-full rounded-br-md rounded-bl-md text-center font-semibold"
          @click.prevent="search(close)"
        >
          Search
        </button>
      </PopoverPanel>
    </TransitionRoot>
  </Popover>
</template>
